import React, { useState, useEffect, useContext } from 'react';
import { classHistory, getOfflineStudentSlots, getTeachersOffline, getTeacherSlots, rescheduleOfflineSlot } from '../../../../../Api/apiUtils';
import {
    Button,
    Table,
    Field,
    Select,
    SearchInput,
    TextInput,
    Toolbar,
    DescriptionList,
    EmptyState,
} from '../../../../../ui';
import { Dialog, TextArea, FormGrid, Callout, StudentStatusBadge } from '../ui-pending';
import { formatDate } from '../../../../../utils/formatDate';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ConfigContext } from '../../../../../Api/global/ApiContext';
import { toast } from 'react-toastify';
import { isTeacherLeaderDisabled } from '../../../../../utils/permissions';
import './style.scss';

interface ClassHistoryProps {
    courseId: any;
    studentId: any;
    selectedCourseData:any
}

interface ClassHistoryItem {
    actionPerformed: string | null;
    classId: string;
    statusName: string;
    actionDateTime: string;
    slotDate: string;
    startTime: string;
    studentJoinTime: string | null;
    studentLeftTime: string | null;
    teacherJoinTime: string | null;
    teacherLeftTime: string | null;
    comment?: string;
}

interface OfflineClassItem {
    classNumber: number;
    schedule: string;
    attendanceStatus: 'Attended' | 'Absent' | 'Scheduled' | string;
    action: string | null;
    comment: string;
    slotDate?: string;
    startTime?: string;
    slotId?: number;
    studentSlotId?: number;
    centerName?: string;
    city?: string;
}

interface Teacher {
    teacherId?: string | number;
    id?: string | number;
    userId:any;
    name?: string;
    teacherName?: string;
}

const ClassHistory: React.FC<ClassHistoryProps> = ({ studentId, courseId, selectedCourseData }) => {
    console.log("classHistory", selectedCourseData);
    const config = useContext(ConfigContext);
    const readOnly = isTeacherLeaderDisabled();
    const [history, setHistory] = useState<ClassHistoryItem[]>([]);
    const [filteredHistory, setFilteredHistory] = useState<ClassHistoryItem[]>([]);
    const [selectedClassId, setSelectedClassId] = useState<string>('all');
    const [uniqueClassIds, setUniqueClassIds] = useState<string[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedClassDetails, setSelectedClassDetails] = useState<ClassHistoryItem | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [offlineClasses, setOfflineClasses] = useState<OfflineClassItem[]>([]);
    
    // Reschedule modal state
    const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
    const [selectedClassForReschedule, setSelectedClassForReschedule] = useState<OfflineClassItem | null>(null);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [loadingTeachers, setLoadingTeachers] = useState<boolean>(false);
    const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string>('');
    const [rescheduleComment, setRescheduleComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [availableSlots, setAvailableSlots] = useState<any[]>([]);
    const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
    const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
    const [selectedVirtualSlotId, setSelectedVirtualSlotId] = useState<string | null>(null);

    useEffect(() => {
        const fetchClassHistory = async () => {
            // If it's an offline batch course, use the offline API
            if (selectedCourseData?.batchOfflineCourse) {
                try {
                    const response = await getOfflineStudentSlots(selectedCourseData.studentId || studentId, courseId, 0, 100);
                    let slots = response.slots || [];
                    
                    // Sort slots by date and time (oldest first)
                    slots = slots.sort((a: any, b: any) => {
                        const dateA = new Date(`${a.slotDate}T${a.startTime}`).getTime();
                        const dateB = new Date(`${b.slotDate}T${b.startTime}`).getTime();
                        return dateA - dateB;
                    });
                    
                    // Transform API data to OfflineClassItem format
                    const transformedClasses: OfflineClassItem[] = slots.map((slot: any, index: number) => {
                        // Use attendanceStatus directly from API without any mapping
                        // Possible values: PENDING, MARKED_PRESENT, MARKED_ABSENT, TRANSFERRED_AWAY, GUEST_PRESENT, GUEST_ABSENT, EXCUSED
                        const attendanceStatus = slot.attendanceStatus || 'PENDING';

                        // Format schedule: "3 PM - 1 Oct"
                        const date = slot.slotDate ? new Date(slot.slotDate) : null;
                        const timeStr = slot.startTime || '';
                        const [hours, minutes] = timeStr.split(':');
                        const hour24 = parseInt(hours || '0');
                        const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
                        const ampm = hour24 >= 12 ? 'PM' : 'AM';
                        const timeFormatted = `${hour12} ${ampm}`;
                        
                        const dateFormatted = date ? `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })}` : '';
                        const schedule = dateFormatted ? `${timeFormatted} - ${dateFormatted}` : '-';

                        // Determine action
                        let action: string | null = null;
                        if (slot.slotStatus === 'RESCHEDULED') {
                            action = 'Rescheduled';
                        } else if (attendanceStatus === 'PENDING') {
                            action = 'Reschedule';
                        }

                        return {
                            classNumber: index + 1,
                            schedule: schedule,
                            attendanceStatus: attendanceStatus,
                            action: action,
                            comment: slot.additionalNotes || '',
                            slotDate: slot.slotDate,
                            startTime: slot.startTime,
                            slotId: slot.slotId,
                            studentSlotId: slot.studentSlotId,
                            centerName: slot.centerName,
                            city: slot.city
                        };
                    });
                    setOfflineClasses(transformedClasses);
                } catch (error) {
                    console.error('Error fetching offline student slots:', error);
                    setOfflineClasses([]);
                }
            } else {
                // Use regular class history API for non-offline courses
                const response = await classHistory(studentId, courseId);
                setHistory(response);
                
                // Extract unique class IDs
                const classIds = response
                    .map((item: ClassHistoryItem) => item.classId)
                    .filter((value: any, index: any, self: any) => self.indexOf(value) === index);
                setUniqueClassIds(classIds);
            }
        };
        fetchClassHistory();
    }, [studentId, courseId, selectedCourseData]);

    useEffect(() => {
        let result = history;
        
        // First filter by selected class ID
        if (selectedClassId !== 'all') {
            result = result.filter(item => item.classId === selectedClassId);
        }
        
        // Then filter by search term if it exists
        if (searchTerm) {
            result = result.filter(item => 
                item.classId.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        setFilteredHistory(result);
    }, [selectedClassId, history, searchTerm]);

    const formatTime = (timeString: string | null) => {
        if (!timeString) return '-';
        const [hours, minutes] = timeString.split(':');
        return `${hours}:${minutes}`;
    };

    const openModal = (item: ClassHistoryItem) => {
        setSelectedClassDetails(item);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    // Generate time options (30-minute intervals)
    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
                times.push(timeString);
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    const handleReschedule = (classItem: OfflineClassItem) => {
        setSelectedClassForReschedule(classItem);
        setCurrentStep(1);
        setSelectedTeacher(null);
        setSelectedDate(null);
        setSelectedTime('');
        setRescheduleComment('');
        setSelectedSlotId(null);
        setSelectedVirtualSlotId(null);
        setRescheduleModalOpen(true);
    };
    const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
    useEffect(() => {
        if (rescheduleModalOpen && selectedClassForReschedule && currentStep === 1 && config?.offlineCentres) {
            fetchTeachersForReschedule();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rescheduleModalOpen, selectedClassForReschedule, currentStep]);

    // Fetch available slots when teacher and date are selected
    useEffect(() => {
        if (selectedTeacher && selectedDate && currentStep === 1) {
            fetchAvailableSlots();
        } else if (!selectedTeacher || !selectedDate) {
            // Only clear slots and time if teacher or date is cleared, not when step changes
            setAvailableSlots([]);
            setSelectedTime('');
            setSelectedSlotId(null);
            setSelectedVirtualSlotId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTeacher, selectedDate, currentStep]);

    const fetchAvailableSlots = async () => {
        if (!selectedTeacher?.teacherId || !selectedDate) return;
        
        setLoadingSlots(true);
        try {
            const fromDate = formatDateLocal(selectedDate);
            const toDate = formatDateLocal(selectedDate);
            
            const response = await getTeacherSlots(selectedTeacher.userId, fromDate, toDate, 0, 100);
            const slots = response?.slots || [];
            
            // Filter slots for the selected date that are available
            const slotsForDate = slots.filter((slot: any) => slot.slotDate === fromDate && slot.available);
            
            // Sort by start time
            slotsForDate.sort((a: any, b: any) => {
                return (a.startTime || '').localeCompare(b.startTime || '');
            });
            
            // Store full slot data (not just times) so we can get slotId when time is selected
            setAvailableSlots(slotsForDate);
        } catch (error) {
            console.error('Error fetching available slots:', error);
            toast.error('Failed to fetch available slots');
            setAvailableSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const fetchTeachersForReschedule = async () => {
        if (!selectedClassForReschedule || !config?.offlineCentres) return;
        
        setLoadingTeachers(true);
        try {
            // Find centerId from config using centerName or city
            const center = config.offlineCentres.find((c: any) => 
                c.name === selectedClassForReschedule.centerName || 
                c.centreName === selectedClassForReschedule.centerName ||
                c.city === selectedClassForReschedule.city
            );
            
            const centerId = center?.centreId || center?.centerId;
            
            if (!centerId) {
                toast.error('Center information not found');
                setLoadingTeachers(false);
                return;
            }

            const response = await getTeachersOffline(centerId, courseId);
            let teachersData: any[] = [];
            
            if (response?.content && Array.isArray(response.content)) {
                teachersData = response.content;
            } else if (Array.isArray(response)) {
                teachersData = response;
            } else if (response?.data) {
                if (Array.isArray(response.data)) {
                    teachersData = response.data;
                } else if (response.data.content && Array.isArray(response.data.content)) {
                    teachersData = response.data.content;
                }
            }
            
            const normalizedTeachers = teachersData.map((teacher: any) => ({
                ...teacher,
                teacherId: teacher.teacherId || teacher.id,
                name: teacher.name || teacher.teacherName,
            }));
            
            setTeachers(normalizedTeachers);
        } catch (error) {
            console.error('Error fetching teachers:', error);
            toast.error('Failed to fetch teachers');
            setTeachers([]);
        } finally {
            setLoadingTeachers(false);
        }
    };

    const closeRescheduleModal = () => {
        setRescheduleModalOpen(false);
        setSelectedClassForReschedule(null);
        setCurrentStep(1);
        setSelectedTeacher(null);
        setSelectedDate(null);
        setSelectedTime('');
        setRescheduleComment('');
        setAvailableSlots([]);
        setSelectedSlotId(null);
        setSelectedVirtualSlotId(null);
    };

    const handleNextStep = () => {
        if (currentStep === 1) {
            if (!selectedTeacher || !selectedDate || !selectedTime) {
                toast.error('Please select teacher, date, and time');
                return;
            }
        }
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleConfirmReschedule = async () => {
        if (!selectedClassForReschedule?.studentSlotId || (!selectedSlotId && !selectedVirtualSlotId)) {
            toast.error('Missing required information for rescheduling');
            return;
        }

        setIsSubmitting(true);
        try {
            await rescheduleOfflineSlot(
                selectedClassForReschedule.studentSlotId,
                selectedSlotId,
                selectedVirtualSlotId,
                rescheduleComment || ''
            );
            
            setCurrentStep(4);
            toast.success('Class rescheduled successfully!');
            
            // Refresh the class history to show updated data
            const response = await getOfflineStudentSlots(selectedCourseData.studentId || studentId, courseId, 0, 100);
            let slots = response.slots || [];
            slots = slots.sort((a: any, b: any) => {
                const dateA = new Date(`${a.slotDate}T${a.startTime}`).getTime();
                const dateB = new Date(`${b.slotDate}T${b.startTime}`).getTime();
                return dateA - dateB;
            });
            
            const transformedClasses: OfflineClassItem[] = slots.map((slot: any, index: number) => {
                const attendanceStatus = slot.attendanceStatus || 'PENDING';
                const date = slot.slotDate ? new Date(slot.slotDate) : null;
                const timeStr = slot.startTime || '';
                const [hours, minutes] = timeStr.split(':');
                const hour24 = parseInt(hours || '0');
                const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
                const ampm = hour24 >= 12 ? 'PM' : 'AM';
                const timeFormatted = `${hour12} ${ampm}`;
                const dateFormatted = date ? `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })}` : '';
                const schedule = dateFormatted ? `${timeFormatted} - ${dateFormatted}` : '-';
                
                let action: string | null = null;
                if (slot.slotStatus === 'RESCHEDULED') {
                    action = 'Rescheduled';
                } else if (attendanceStatus === 'PENDING') {
                    action = 'Reschedule';
                }
                
                return {
                    classNumber: index + 1,
                    schedule: schedule,
                    attendanceStatus: attendanceStatus,
                    action: action,
                    comment: slot.additionalNotes || '',
                    slotDate: slot.slotDate,
                    startTime: slot.startTime,
                    slotId: slot.slotId,
                    studentSlotId: slot.studentSlotId,
                    centerName: slot.centerName,
                    city: slot.city
                };
            });
            setOfflineClasses(transformedClasses);
        } catch (error: any) {
            console.error('Error rescheduling class:', error);
            toast.error(error?.response?.data?.message || 'Failed to reschedule class');
        } finally {
            setIsSubmitting(false);
        }
    };


    // Offline attendance status -> shared status key (badge tone); the label stays the raw backend value.
    const attendanceStatusKey = (status: string) =>
        status === 'MARKED_PRESENT' || status === 'GUEST_PRESENT' ? 'attended' :
        status === 'MARKED_ABSENT' || status === 'GUEST_ABSENT' ? 'absent' :
        status === 'PENDING' ? 'scheduled' :
        status;

    // Render offline batch course UI
    if (selectedCourseData?.batchOfflineCourse) {
        return (
            <div className="class-history-container">
                {offlineClasses.length > 0 ? (
                    <Table className="class-history-table" caption="Offline class history">
                        <thead>
                            <tr>
                                <th>Class Number</th>
                                <th>Class Schedule</th>
                                <th>Attendance Status</th>
                                <th>Action</th>
                                <th>Comments</th>
                            </tr>
                        </thead>
                        <tbody>
                            {offlineClasses.map((classItem, index) => (
                                <tr key={index}>
                                    <td>Class {classItem.classNumber}</td>
                                    <td>{classItem.schedule}</td>
                                    <td>
                                        <StudentStatusBadge
                                            status={attendanceStatusKey(classItem.attendanceStatus)}
                                            label={classItem.attendanceStatus}
                                        />
                                    </td>
                                    <td>
                                        {classItem.action ? (
                                            classItem.action === 'Rescheduled' ? (
                                                <StudentStatusBadge status="rescheduled" label={classItem.action} />
                                            ) : (
                                                <Button
                                                    variant="accent"
                                                    size="sm"
                                                    onClick={() => {
                                                      if (readOnly) return;
                                                      handleReschedule(classItem);
                                                    }}
                                                    disabled={readOnly}
                                                    title={readOnly ? "Disabled for Teacher Leaders" : undefined}
                                                    aria-label={`${classItem.action} class ${classItem.classNumber}`}
                                                >
                                                    {classItem.action}
                                                </Button>
                                            )
                                        ) : (
                                            <span className="ds-text-subtle">-</span>
                                        )}
                                    </td>
                                    <td className={classItem.comment ? undefined : 'ds-text-subtle'}>
                                        {classItem.comment || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <EmptyState
                        title="No class history available"
                        description="Classes in this offline batch will appear here once they are scheduled."
                    />
                )}

                {/* Reschedule Modal */}
                <Dialog
                    isOpen={rescheduleModalOpen}
                    onClose={closeRescheduleModal}
                    size="lg"
                    title="Reschedule Class"
                    subtitle={currentStep < 4 ? `Step ${currentStep} of 3` : undefined}
                    actions={
                        currentStep < 4 ? (
                            <>
                                <Button variant="secondary" onClick={currentStep === 1 ? closeRescheduleModal : handlePreviousStep}>
                                    {currentStep === 1 ? 'Cancel' : 'Previous'}
                                </Button>
                                {currentStep === 3 ? (
                                    <Button
                                        variant="primary"
                                        onClick={handleConfirmReschedule}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : 'Save'}
                                    </Button>
                                ) : (
                                    <Button variant="primary" onClick={handleNextStep}>
                                        Next
                                    </Button>
                                )}
                            </>
                        ) : (
                            <Button variant="primary" onClick={closeRescheduleModal}>
                                Close
                            </Button>
                        )
                    }
                >
                    {/* Step 1: Select Teacher and Day/Time */}
                    {currentStep === 1 && (
                        <div className="ds-stack class-history-steps">
                            <Field
                                label="Step 1: Select Teacher"
                                hint={loadingTeachers ? 'Loading teachers...' : undefined}
                            >
                                {(ctl) => (
                                    <Select
                                        {...ctl}
                                        options={[
                                            { value: '', label: 'Select Teacher' },
                                            ...teachers.map((teacher) => ({
                                                value: String(teacher.teacherId || teacher.id),
                                                label: String(teacher.name || teacher.teacherName || ''),
                                            })),
                                        ]}
                                        value={String(selectedTeacher?.teacherId || '')}
                                        onChange={(e) => {
                                            const teacher = teachers.find(t =>
                                                (t.teacherId || t.id)?.toString() === e.target.value
                                            );
                                            setSelectedTeacher(teacher || null);
                                        }}
                                        disabled={loadingTeachers}
                                    />
                                )}
                            </Field>
                            <p className="ds-strong class-history-steps__heading">Select Any Day / Time to Reschedule</p>
                            <FormGrid min={240}>
                                <Field label="Date">
                                    {(ctl) => (
                                        <DatePicker
                                            id={ctl.id}
                                            selected={selectedDate}
                                            onChange={(date: Date | null) => setSelectedDate(date)}
                                            minDate={new Date()}
                                            dateFormat="yyyy-MM-dd"
                                            customInput={<TextInput />}
                                            placeholderText="Select date"
                                        />
                                    )}
                                </Field>
                                <Field
                                    label="Available Slots"
                                    hint={
                                        loadingSlots
                                            ? 'Loading available slots...'
                                            : availableSlots.length === 0 && selectedTeacher && selectedDate && !loadingSlots
                                                ? 'No available slots'
                                                : undefined
                                    }
                                >
                                    {(ctl) => (
                                        <Select
                                            {...ctl}
                                            options={[
                                                {
                                                    value: '',
                                                    label: loadingSlots ? 'Loading slots...' : !selectedTeacher || !selectedDate ? 'Select teacher and date first' : 'Select time',
                                                },
                                                ...availableSlots.map((slot: any) => {
                                                    // Format time from "HH:MM:SS" to "HH:MM"
                                                    const timeFormatted = slot.startTime ? slot.startTime.substring(0, 5) : '';
                                                    return { value: timeFormatted, label: timeFormatted };
                                                }),
                                            ]}
                                            value={selectedTime}
                                            onChange={(e) => {
                                                setSelectedTime(e.target.value);
                                                // Find the slotId for the selected time
                                                const selectedSlot = availableSlots.find((slot: any) =>
                                                    slot.startTime && slot.startTime.substring(0, 5) === e.target.value
                                                );
                                                setSelectedSlotId(selectedSlot?.slotId || null);
                                                setSelectedVirtualSlotId(selectedSlot?.virtualSlotId || null);
                                            }}
                                            disabled={!selectedTeacher || !selectedDate || loadingSlots}
                                        />
                                    )}
                                </Field>
                            </FormGrid>
                        </div>
                    )}

                    {/* Step 2: Comments */}
                    {currentStep === 2 && (
                        <Field label="Step 2: Comments">
                            {(ctl) => (
                                <TextArea
                                    {...ctl}
                                    rows={4}
                                    value={rescheduleComment}
                                    onChange={(e) => setRescheduleComment(e.target.value)}
                                    placeholder="Enter comments (optional)"
                                />
                            )}
                        </Field>
                    )}

                    {/* Step 3: Confirm Action */}
                    {currentStep === 3 && (
                        <div className="ds-stack">
                            <p className="ds-strong class-history-steps__heading">Step 3: Confirm Action</p>
                            <DescriptionList
                                items={[
                                    { label: 'Class', value: `Class ${selectedClassForReschedule?.classNumber}` },
                                    { label: 'Original Schedule', value: selectedClassForReschedule?.schedule },
                                    { label: 'New Teacher', value: selectedTeacher?.name || selectedTeacher?.teacherName },
                                    { label: 'New Date', value: selectedDate ? selectedDate.toLocaleDateString() : '-' },
                                    {
                                        label: 'New Time',
                                        value: selectedTime ? (() => {
                                            // Format time from "HH:MM" to "H:MM AM/PM"
                                            const [hours, minutes] = selectedTime.split(':');
                                            const hour24 = parseInt(hours || '0');
                                            const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
                                            const ampm = hour24 >= 12 ? 'PM' : 'AM';
                                            return `${hour12}:${minutes} ${ampm}`;
                                        })() : '-',
                                    },
                                    ...(rescheduleComment ? [{ label: 'Comments', value: rescheduleComment }] : []),
                                ]}
                            />
                        </div>
                    )}

                    {/* Step 4: Success Screen */}
                    {currentStep === 4 && (
                        <Callout tone="success" title="Success!">
                            <p className="class-history-steps__text">Class has been rescheduled successfully.</p>
                            <p className="class-history-steps__text">
                                <span className="ds-strong">New Schedule:</span> {
                                    selectedDate && selectedTime ? (() => {
                                        const dateStr = selectedDate.toLocaleDateString();
                                        // Format time from "HH:MM" to "H:MM AM/PM"
                                        const [hours, minutes] = selectedTime.split(':');
                                        const hour24 = parseInt(hours || '0');
                                        const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
                                        const ampm = hour24 >= 12 ? 'PM' : 'AM';
                                        return `${dateStr} at ${hour12}:${minutes} ${ampm}`;
                                    })() : ''
                                }
                            </p>
                        </Callout>
                    )}
                </Dialog>
            </div>
        );
    }

    // Original UI for non-offline batch courses
    return (
        <div className="class-history-container">
            <Toolbar>
                <Field label="Class">
                    {(ctl) => (
                        <Select
                            {...ctl}
                            options={[
                                { value: 'all', label: 'All Classes' },
                                ...uniqueClassIds.map((classId) => ({ value: classId, label: classId })),
                            ]}
                            value={selectedClassId}
                            onChange={(e) => {
                                setSelectedClassId(e.target.value);
                                setSearchTerm(''); // Clear search when selecting from dropdown
                            }}
                        />
                    )}
                </Field>
                <Field label="Search" className="ds-toolbar__grow">
                    {(ctl) => (
                        <SearchInput
                            {...ctl}
                            placeholder="Search Class ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    )}
                </Field>
            </Toolbar>

            {filteredHistory.length > 0 ? (
                <Table className="class-history-table" caption="Class history">
                    <thead>
                        <tr>
                            <th>Action Date & Time</th>
                            <th>Class ID</th>
                            <th>Action</th>
                            <th>Action Taken By</th>
                            <th>Class Date & Time</th>
                            <th>View Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredHistory.map((item, index) => (
                            <tr key={index}>
                                <td>{formatDate(item.actionDateTime)}</td>
                                <td>{item.classId}</td>
                                <td>{item.statusName}</td>
                                <td>{item.actionPerformed || 'System'}</td>
                                <td>
                                    {formatDate(item.slotDate, "shortDate")} ,{" "}
                                    {formatDate(`${item.slotDate}T${item.startTime}`, "timeOnly")}
                                </td>
                                <td>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => openModal(item)}
                                        aria-label={`View details for class ${item.classId}`}
                                    >
                                        View
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <EmptyState
                    title="No class history available"
                    description="Try All Classes or clear the Class ID search."
                />
            )}

            {/* Modal for showing class details */}
            <Dialog isOpen={modalOpen} onClose={closeModal} title="Class Details">
                {selectedClassDetails && (
                    <DescriptionList
                        items={[
                            { label: 'Teacher Joined Time', value: selectedClassDetails.teacherJoinTime ? formatDate(selectedClassDetails.teacherJoinTime) : '-' },
                            { label: 'Teacher Left Time', value: selectedClassDetails.teacherLeftTime ? formatDate(selectedClassDetails.teacherLeftTime) : '-' },
                            { label: 'Student Joined Time', value: selectedClassDetails.studentJoinTime ? formatDate(selectedClassDetails.studentJoinTime) : '-' },
                            { label: 'Student Left Time', value: selectedClassDetails.studentLeftTime ? formatDate(selectedClassDetails.studentLeftTime) : '-' },
                        ]}
                    />
                )}
            </Dialog>
        </div>
    );
};

export default ClassHistory;